import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Swords, ArrowLeft, RefreshCw, Megaphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  adminListTournaments, adminUpdateTournament, adminGenerateBracket,
  adminUpdateMatch, getTournamentBracket, adminNotify, formatApiError,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";

function MatchRow({ match, onSaved }) {
  const [a, setA] = useState(match.score_a ?? "");
  const [b, setB] = useState(match.score_b ?? "");
  const [winner, setWinner] = useState(match.winner ?? "");
  const [status, setStatus] = useState(match.status ?? "upcoming");
  const [saving, setSaving] = useState(false);
  const hasTeams =
    match.team_a?.name && match.team_a.name !== "TBD" &&
    match.team_b?.name && match.team_b.name !== "TBD";

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateMatch(match.id, {
        score_a: a === "" ? null : parseInt(a, 10),
        score_b: b === "" ? null : parseInt(b, 10),
        winner: winner || "",
        status,
      });
      toast.success(`Match ${match.id} saved.`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-white/10 cut-corners p-3 bg-black/40 flex flex-wrap items-center gap-3"
      data-testid={`admin-match-${match.id}`}>
      <span className="text-[10px] font-bold uppercase tracking-widest text-[#D4AF37] w-14">{match.id}</span>
      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
        <span className="text-xs font-display uppercase truncate flex-1">{match.team_a?.name || "TBD"}</span>
        <Input type="number" value={a} onChange={(e) => setA(e.target.value)}
          disabled={!hasTeams} data-testid={`admin-score-a-${match.id}`}
          className="w-16 bg-black border-white/15" placeholder="—" />
      </div>
      <div className="flex items-center gap-2 flex-1 min-w-[220px]">
        <span className="text-xs font-display uppercase truncate flex-1">{match.team_b?.name || "TBD"}</span>
        <Input type="number" value={b} onChange={(e) => setB(e.target.value)}
          disabled={!hasTeams} data-testid={`admin-score-b-${match.id}`}
          className="w-16 bg-black border-white/15" placeholder="—" />
      </div>
      <Select value={winner || "none"} onValueChange={(v) => setWinner(v === "none" ? "" : v)}>
        <SelectTrigger data-testid={`admin-winner-${match.id}`}
          className="w-28 bg-black border-white/15" disabled={!hasTeams}>
          <SelectValue placeholder="Winner" />
        </SelectTrigger>
        <SelectContent className="bg-[#0A0A0A] border-white/15 text-white">
          <SelectItem value="none">—</SelectItem>
          <SelectItem value="a">Team A</SelectItem>
          <SelectItem value="b">Team B</SelectItem>
        </SelectContent>
      </Select>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid={`admin-status-${match.id}`} className="w-32 bg-black border-white/15">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0A0A0A] border-white/15 text-white">
          <SelectItem value="upcoming">Upcoming</SelectItem>
          <SelectItem value="live">Live</SelectItem>
          <SelectItem value="final">Final</SelectItem>
        </SelectContent>
      </Select>
      <Button onClick={save} disabled={saving || !hasTeams} data-testid={`admin-save-${match.id}`}
        className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners text-xs">
        {saving ? "..." : "Save"}
      </Button>
    </div>
  );
}

function TournamentManager({ tournament, onChanged }) {
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const load = () => {
    setLoading(true);
    getTournamentBracket(tournament.id)
      .then(setBracket)
      .catch(() => setBracket(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tournament.id]);

  const setStatus = async (status) => {
    try {
      await adminUpdateTournament(tournament.id, { status });
      toast.success(`${tournament.name} is now ${status}.`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await adminGenerateBracket(tournament.id);
      toast.success("Bracket regenerated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRegenerating(false); }
  };

  return (
    <div className="tgl-card cut-corners p-6 space-y-5" data-testid={`admin-card-${tournament.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-xl font-black uppercase text-white">{tournament.name}</h3>
          <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mt-1">
            {tournament.game} · {tournament.registered}/{tournament.slots} squads
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={tournament.status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`admin-tournament-status-${tournament.id}`}
              className="w-36 bg-black border-white/15">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/15 text-white">
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="ended">Ended</SelectItem>
            </SelectContent>
          </Select>
          {tournament.has_bracket && (
            <Button onClick={regenerate} disabled={regenerating}
              data-testid={`admin-regenerate-${tournament.id}`}
              variant="outline"
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
              <RefreshCw size={14} className="mr-2" />
              {regenerating ? "..." : "Regenerate Bracket"}
            </Button>
          )}
        </div>
      </div>

      {tournament.has_bracket && (
        <>
          {loading && <p className="text-zinc-500 text-sm">Loading bracket...</p>}
          {!loading && bracket && !bracket.locked && (
            <p className="text-sm text-zinc-400 border border-white/10 p-3 cut-corners">{bracket.reason}</p>
          )}
          {!loading && bracket && bracket.locked && (
            <div className="space-y-6">
              {bracket.rounds.map((r) => (
                <div key={r.round}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Round {r.round}</p>
                  <div className="space-y-2">
                    {r.matches.map((m) => (
                      <MatchRow key={m.id} match={m} onSaved={load} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NotifyPanel() {
  const [form, setForm] = useState({ title: "", body: "", target: "all" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await adminNotify(form);
      toast.success("Dispatch sent.");
      setForm({ title: "", body: "", target: "all" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  return (
    <div className="tgl-card cut-corners p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Megaphone size={16} className="text-[#D4AF37]" />
        <h3 className="font-display uppercase text-lg">Broadcast Dispatch</h3>
      </div>
      <form onSubmit={submit} className="grid gap-3">
        <Input data-testid="notify-title" value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required placeholder="Title"
          className="bg-black border-white/15 focus-visible:ring-[#D4AF37]" />
        <Textarea data-testid="notify-body" value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          required placeholder="Body"
          className="bg-black border-white/15 focus-visible:ring-[#D4AF37]" />
        <Input value={form.target}
          onChange={(e) => setForm({ ...form, target: e.target.value })}
          placeholder="all | tournament:<id> | team:<id>"
          className="bg-black border-white/15 focus-visible:ring-[#D4AF37]" />
        <Button type="submit" disabled={sending} data-testid="notify-submit"
          className="bg-[#D4AF37] hover:bg-[#B39028] text-black font-bold uppercase tracking-widest cut-corners w-fit">
          {sending ? "Sending..." : "Send Dispatch"}
        </Button>
      </form>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    adminListTournaments()
      .then(setTournaments)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user?.user_id, user?.role]);

  if (authLoading) {
    return <div className="min-h-screen grid place-items-center bg-[#050505] text-zinc-400">Authenticating...</div>;
  }
  if (!user) return <Navigate to="/auth" replace state={{ from: "/admin" }} />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#050505] text-zinc-400 px-6">
        <div className="text-center">
          <h1 className="font-display text-2xl text-white mb-2">Admins Only</h1>
          <p className="mb-6">You don't have access to this throne room.</p>
          <Link to="/" className="text-[#D4AF37] underline">Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white py-10 px-6 pt-24">
      <Navbar />
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#D4AF37]">⌁ Control Panel</p>
            <h1 className="font-display text-3xl lg:text-4xl font-black uppercase tracking-tight">
              TGL <span className="gold-text">Admin</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-home"
              className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#D4AF37]">
              <ArrowLeft size={12} className="inline mr-1" /> Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] text-zinc-300 px-3 py-2 cut-corners text-xs uppercase tracking-widest">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>

        <NotifyPanel />

        <div className="flex items-center gap-2 mb-6 text-zinc-400 text-sm">
          <Swords size={16} className="text-[#D4AF37]" />
          <span>Tournaments & Brackets</span>
        </div>

        {loading && <p className="text-zinc-500">Loading...</p>}
        <div className="space-y-6">
          {tournaments.map((t) => (
            <TournamentManager key={t.id} tournament={t} onChanged={load} />
          ))}
        </div>
      </div>
    </div>
  );
}
